import type { ReactNode } from "react";
import styled from "styled-components";
import { PAYMENT_TERMS_TITLE, SHOW_ADMINISTRATION_FEE } from "./administrationFee";

const PURPLE = "#3a0ca3";
const ACCENT = "#6936dd";
const TEXT = "#303030";
const MUTED = "#707070";

/*
 * Compact rules apply on narrow screens and, via @container, when the document sits in a narrow
 * column on a wider screen (e.g. the creator's Campaign Info panel). Hosts opt in with
 * `container-type: inline-size`; without a container only the viewport rule applies.
 */
const narrow = (rules: string) => `
	@media (max-width: 700px) {
		${rules}
	}

	@container (max-width: 700px) {
		${rules}
	}
`;

const Document = styled.article<{ $embedded: boolean }>`
	box-sizing: border-box;
	width: 100%;
	padding: ${({ $embedded }) => ($embedded ? "32px" : "80px")};
	display: flex;
	flex-direction: column;
	gap: 40px;
	background: ${({ $embedded }) => ($embedded ? "transparent" : "#f3f5ff")};
	color: ${TEXT};
	font-family: Poppins, sans-serif;
	font-size: 14px;
	line-height: 24px;

	* {
		box-sizing: border-box;
	}

	${({ $embedded }) => narrow(`padding: ${$embedded ? "20px 16px" : "24px 16px"}; gap: 28px;`)}
`;

const Header = styled.header`
	padding-bottom: 24px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 8px 24px;

	${narrow("padding-bottom: 8px;")}
`;

const Logo = styled.img`
	display: block;
	width: 240px;
	height: auto;
	max-width: 65%;
`;

const Eyebrow = styled.span`
	color: ${ACCENT};
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
`;

const Preamble = styled.section`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const PreambleTitle = styled.h2`
	margin: 0;
	color: ${PURPLE};
	font-size: 14px;
	line-height: 24px;
	font-weight: 600;
`;

const Copy = styled.p`
	margin: 0;
`;

const Warning = styled.div`
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	border-radius: 12px;
	background: #ebeefd;

	a {
		color: inherit;
		font-weight: 600;
	}

	${Copy}:last-child {
		color: ${MUTED};
	}
`;

const TitleBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const Title = styled.h1`
	margin: 0;
	color: ${TEXT};
	font-size: 38px;
	line-height: 1.25;
	font-weight: 700;

	${narrow("font-size: 24px;")}
`;

const Metadata = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	flex-wrap: wrap;
`;

const Version = styled.span`
	padding: 4px 12px;
	border-radius: 999px;
	background: ${ACCENT};
	color: #f3f5ff;
	font-size: 12px;
	line-height: normal;
	font-weight: 500;
`;

const Company = styled.strong`
	font-weight: 600;
`;

const Address = styled.span`
	color: ${MUTED};
`;

const DocumentDates = styled.p`
	margin: 0;
	color: ${MUTED};
`;

const Divider = styled.hr`
	width: 100%;
	height: 1px;
	margin: 0;
	border: 0;
	background: #cdd4f6;
`;

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const SectionHeading = styled.div`
	display: grid;
	grid-template-columns: 40px minmax(0, 1fr);
	gap: 16px;

	${narrow("grid-template-columns: 36px minmax(0, 1fr); gap: 8px;")}
	color: ${PURPLE};
	font-weight: 600;
`;

const Heading = styled.h2`
	margin: 0;
	font: inherit;
`;

const Clauses = styled.div`
	padding-left: 56px;
	display: flex;
	flex-direction: column;
	gap: 12px;

	${narrow("padding-left: 0;")}
`;

const Clause = styled.div`
	display: grid;
	grid-template-columns: 40px minmax(0, 1fr);
	gap: 16px;

	${narrow("grid-template-columns: 36px minmax(0, 1fr); gap: 8px;")}
`;

const ClauseBody = styled.div`
	min-width: 0;

	p {
		margin: 0;
	}
`;

const Subheading = styled.h3`
	margin: 4px 0 0;
	font-size: 14px;
	line-height: 24px;
	font-weight: 600;
`;

const Sublist = styled.div`
	padding-left: 48px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	color: ${MUTED};

	${narrow("padding-left: 20px;")}
`;

const TableWrap = styled.div`
	width: 100%;
	overflow-x: auto;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
`;

const Table = styled.table<{ $workedExample?: boolean }>`
	width: 100%;
	min-width: 600px;
	border-collapse: collapse;
	background: transparent;

	th,
	td {
		padding: 10px 16px;
		border-bottom: 1px solid #cdd4f6;
		text-align: left;
		vertical-align: top;
	}

	th {
		background: #dde2fa;
		color: #011992;
		font-weight: 600;
	}

	tr:last-child td {
		border-bottom: 0;
	}

	th:first-child,
	td:first-child {
		width: ${({ $workedExample }) => ($workedExample ? "auto" : "76px")};
	}

	${({ $workedExample }) =>
		$workedExample
			? `
		td:last-child {
			width: 150px;
			white-space: nowrap;
			font-weight: 500;
		}
	`
			: ""}
	}
`;

const Definitions = styled.ul`
	margin: 0;
	padding-left: 21px;
`;

type ClauseData = {
	number: string;
	content: ReactNode;
};

const LegalSection = ({ number, title, children }: { number: string; title: string; children: ReactNode }) => (
	<Section>
		<SectionHeading>
			<span>{number}.</span>
			<Heading>{title}</Heading>
		</SectionHeading>
		<Clauses>{children}</Clauses>
	</Section>
);

const ClauseList = ({ clauses }: { clauses: ClauseData[] }) => (
	<>
		{clauses.map((clause) => (
			<Clause key={clause.number}>
				<span>{clause.number}</span>
				<ClauseBody>{clause.content}</ClauseBody>
			</Clause>
		))}
	</>
);

const sectionTwo: ClauseData[] = [
	{ number: "2.1", content: "You must accept these Payment Terms before we can pay you." },
	{
		number: "2.2",
		content:
			"You must accept them yourself. You may not authorise a manager, agent or other person to accept them, to provide your tax details, or to confirm a Payment Statement on your behalf.",
	},
	{ number: "2.3", content: "Your acceptance is recorded with the version, the date and time, and your identity." },
	{
		number: "2.4",
		content:
			"We may amend these Payment Terms at any time by written notice. By clicking “I accept” or continuing to use the Payment Service after notice, or 30 days after notification (whichever is earlier), you agree to the amended Payment Terms.",
	},
];

const sectionFour: ClauseData[] = [
	{
		number: "4.1",
		content: (
			<>
				We charge an <strong>Administration Fee of 10%</strong> for the Payment Service.
			</>
		),
	},
	{
		number: "4.2",
		content: (
			<>
				<strong>The Administration Fee is calculated on the principal amount of your Agreed Fee only.</strong>{" "}
				It is never calculated on any tax component.
			</>
		),
	},
	{
		number: "4.3",
		content:
			"The Administration Fee is not deducted from you. It is charged to the brand or agency as part of the cost of engaging you. You receive your Agreed Fee in full, plus any tax that applies (clause 5).",
	},
	{
		number: "4.4",
		content: "The Administration Fee does not appear on your Payment Statement, because it is not charged to you.",
	},
	{
		number: "4.5",
		content:
			"We may disclose the Administration Fee to our clients as part of the cost of engaging you. This does not change the amount you are paid.",
	},
];

const sectionSix: ClauseData[] = [
	{
		number: "6.1",
		content:
			"We prepare a Payment Statement for each Campaign, from the information you have given us, and present it to you.",
	},
	{
		number: "6.2",
		content: (
			<>
				A Payment Statement is not a tax invoice. Every Payment Statement carries this notice:
				<br />
				<strong>
					This is not a tax invoice. If your country requires you to issue a tax invoice for your services,
					you must issue your own.
				</strong>
			</>
		),
	},
	{
		number: "6.3",
		content:
			"Where the law of your country requires you to issue your own tax invoice or electronic invoice, meeting that requirement is your responsibility. We will tell you where we are aware this applies, but we cannot do it for you. If you choose not to issue your own invoice where your country requires one, that is your responsibility — it does not affect our obligation to pay you and it does not create any liability for us. You may keep and use your Payment Statement as a record for your own purposes.",
	},
	{
		number: "6.4",
		content:
			"We prepare Payment Statements on your behalf as a service to you. We do not do so as your tax agent or adviser.",
	},
	{
		number: "6.5",
		content:
			"Confirming your Payment Statement does not affect when you are paid. We ask you to review and confirm it, and confirming it is how you tell us the details are right — but we will pay you in accordance with clause 7 whether or not you have confirmed it. Nothing about this document holds up your payment.",
	},
	{
		number: "6.6",
		content:
			"By confirming a Payment Statement, you confirm that it is accurate and correctly reflects the agreed engagement and your own tax position. You are responsible for its contents. A later error does not create liability for us for having prepared or processed it, and does not give rise to any claim against us in respect of a payment already made.",
	},
	{
		number: "6.7",
		content:
			"If you consider a Payment Statement incorrect, you may reject it and it will be corrected and re-issued. Rejecting a Payment Statement does not delay or reverse a payment. Where a correction changes the amount owing, the difference is settled on a following payment run. Corrections are subject to clause 5.7 — a change of tax status cannot be applied retrospectively.",
	},
	{
		number: "6.8",
		content:
			"At your request, we will share the tax details shown on your Payment Statement with the brand or agency for the relevant Campaign.",
	},
];

const sectionSeven: ClauseData[] = [
	{
		number: "7.1",
		content: (
			<>
				Payment terms are <strong>60 days from the date your Payment Statement is issued.</strong>
			</>
		),
	},
	{
		number: "7.2",
		content: (
			<>
				Your Payment Statement shows your <strong>payment terms</strong>, not a specific payment date.
			</>
		),
	},
	{
		number: "7.3",
		content:
			"You are paid in your own local currency. We report in USD; the conversion is handled in our finance system and does not change the amount you receive in your own currency.",
	},
	{
		number: "7.4",
		content:
			"Delays caused by incomplete or inaccurate payment information — for example bank details we cannot process — may extend the timeframe.",
	},
	{
		number: "7.5",
		content:
			"We may hold or suspend a payment where we are required or reasonably consider it necessary to do so because of law, sanctions, anti-money-laundering obligations, suspected fraud, a court order, or a requirement of our banking or payment providers. We will tell you that your payment is on hold. This is the only circumstance in which a payment is held.",
	},
	{
		number: "7.6",
		content:
			"Some countries are paid another way. In some countries we pay creators through a local third party rather than directly. Where that applies to you, we will tell you before you accept a Campaign, and the third party’s own process and documentation will apply in place of clauses 6 and 7.",
	},
];

export const PaymentTermsDocument = ({ embedded = false }: { embedded?: boolean }) => (
	<Document $embedded={embedded}>
		<Header>
			{embedded ? (
				<Logo src="/Creatorverse_Wordmark_Purple.svg" alt="Creatorverse" />
			) : (
				<>
					<Logo src="/Creatorverse_Wordmark_Gradient.svg" alt="Creatorverse" />
					<Eyebrow>LEGAL DOCUMENT</Eyebrow>
				</>
			)}
		</Header>

		<Preamble>
			<PreambleTitle>Preamble</PreambleTitle>
			<Copy>
				CREATOR PAYMENTS INTERNATIONAL PTE. LTD. (UEN 202629513M) (“<strong>Creator Payments</strong>”, “we”,
				“us” or “our”) is a company incorporated in Singapore of 69 Circular Road, #02-01, Singapore 049423.
			</Copy>
			<Copy>
				We provide the payment service for the Creatorverse platform (the <strong>Payment Service</strong>). We
				prepare your Payment Statements, hold the bank and tax details needed to pay you, and make your
				payments.
			</Copy>
			<Copy>
				These terms <strong>(Payment Terms)</strong> form our contract with you. When we say{" "}
				<strong>you</strong> or <strong>your</strong>, we mean you as a Talent, or an entity you are authorised
				to accept these Payment Terms for.
			</Copy>
			<Warning data-testid="payment-terms-preamble-warning">
				<Copy>
					<strong>We are a different company from Creatorverse Pte. Ltd.</strong>, which operates the
					Creatorverse platform. Creatorverse Pte. Ltd. is a related company. Your use of the Creatorverse
					platform is governed by its own Platform Terms of Service, which you accept separately.{" "}
					<strong>
						You do not have to accept these Payment Terms to use the Creatorverse platform — you only need
						them if you want to be paid through it.
					</strong>
				</Copy>
				<Copy>
					Your personal data is handled under the{" "}
					<a href="/privacy-policy" target="_blank" rel="noreferrer">
						Privacy Policy
					</a>{" "}
					available on the Platform, which covers both companies.
				</Copy>
			</Warning>
		</Preamble>

		{!embedded && (
			<>
				<TitleBlock>
					<Title>{PAYMENT_TERMS_TITLE}</Title>
					<Metadata>
						<Version>Version 1.1</Version>
						<Company>CREATOR PAYMENTS INTERNATIONAL PTE. LTD.</Company>
						<Address>UEN 202629513M · 69 Circular Road, #02-01, Singapore 049423</Address>
					</Metadata>
					<DocumentDates>Effective date: 1 October 2026 · Last updated: 1 October 2026</DocumentDates>
				</TitleBlock>
				<Divider />
			</>
		)}

		<LegalSection number="1" title="What we do, and what we don’t">
			<Clause>
				<span>1.1</span>
				<ClauseBody>
					<Copy>We provide the Payment Service. That means we:</Copy>
					<Sublist>
						<span>
							(a) collect from you the information needed to pay you — your legal name and details, your
							address, your tax details and your bank details;
						</span>
						<span>
							(b) prepare a Payment Statement for each Campaign, using the information you have given us;
						</span>
						<span>(c) present each Payment Statement to you to review and confirm; and</span>
						<span>(d) pay you.</span>
					</Sublist>
				</ClauseBody>
			</Clause>
			<ClauseList
				clauses={[
					{
						number: "1.2",
						content: (
							<>
								<strong>We are not your accountant, tax agent or adviser.</strong> We do not give tax
								advice and nothing in the Payment Service or on the Platform is tax advice.
							</>
						),
					},
					{
						number: "1.3",
						content:
							"We do not determine your tax position, verify your tax details, or take responsibility for them. Clause 5 sets this out in full.",
					},
					{
						number: "1.4",
						content:
							"We are not responsible for the Creatorverse platform, the campaigns you take part in, or the fees you agree with a Campaign Manager.",
					},
				]}
			/>
		</LegalSection>

		<LegalSection number="2" title="Accepting these Payment Terms">
			<ClauseList clauses={sectionTwo} />
		</LegalSection>

		<LegalSection number="3" title="Information you give us">
			<Clause>
				<span>3.1</span>
				<ClauseBody>Before we can pay you, you must provide and confirm:</ClauseBody>
			</Clause>
			<TableWrap>
				<Table>
					<thead>
						<tr>
							<th aria-label="Number" />
							<th>Field</th>
						</tr>
					</thead>
					<tbody>
						{[
							"Whether you are paid as an individual, a company or a trust, and the full legal name of that person or entity",
							"Your address",
							"Your country of residence",
							"Whether you are registered for goods and services tax (or equivalent) in that country",
							"If you are registered: your corresponding registration number, and the rate that applies to you",
							"Bank name, account name, account number and SWIFT/BIC",
							"Payment currency",
						].map((field, index) => (
							<tr key={field}>
								<td>{index + 1}</td>
								<td>{field}</td>
							</tr>
						))}
					</tbody>
				</Table>
			</TableWrap>
			<ClauseList
				clauses={[
					{
						number: "3.2",
						content: "You confirm this information at the start and re-confirm it for each Campaign.",
					},
					{
						number: "3.3",
						content:
							"You warrant that the information is true, accurate and complete, and that you will update it promptly if it changes. You are responsible for any consequence of information that is wrong, out of date or incomplete.",
					},
					{
						number: "3.4",
						content:
							"Your bank account. The account you nominate must be in your own name, or in the name of the company or trust you are paid as. The account does not have to be in the same country as your address or your country of residence. Where it is not, we will pay the account you have nominated, and you remain responsible for your own tax and reporting obligations in every country that applies to you.",
					},
					{
						number: "3.5",
						content:
							"Your bank and payment details are held in an encrypted profile and passed to our finance function. You consent to us disclosing your payment information to our finance function and our providers for the purpose of processing your payment.",
					},
				]}
			/>
		</LegalSection>

		{SHOW_ADMINISTRATION_FEE && (
			<LegalSection number="4" title="The Administration Fee">
				<ClauseList clauses={sectionFour} />
			</LegalSection>
		)}

		<LegalSection number="5" title="Tax">
			<Subheading>How tax works on your payment</Subheading>
			<ClauseList
				clauses={[
					{ number: "5.1", content: "Your Agreed Fee is a principal amount, exclusive of tax." },
					{
						number: "5.2",
						content:
							"If you tell us you are registered for goods and services tax (or equivalent) in your country of residence, we add that tax on top of your Agreed Fee, and you receive the total. We do not deduct it, withhold it, or pay it to your tax authority for you.",
					},
					{
						number: "5.3",
						content:
							"If you tell us you are not registered, no tax is added and no tax component appears on your Payment Statement.",
					},
				]}
			/>
			<Clause>
				<span>5.4</span>
				<ClauseBody>
					Worked example. You agree a fee of AUD 1,000 for a Campaign and tell us you are registered for GST
					in Australia at 10%:
				</ClauseBody>
			</Clause>
			<TableWrap>
				<Table $workedExample>
					<tbody>
						<tr>
							<td>Agreed Fee (principal)</td>
							<td>AUD 1,000.00</td>
						</tr>
						<tr>
							<td>GST added on top, at the rate you gave us</td>
							<td>AUD 100.00</td>
						</tr>
						<tr>
							<td>Total we pay you</td>
							<td>AUD 1,100.00</td>
						</tr>
						<tr>
							<td>GST you are responsible for accounting for to your tax authority</td>
							<td>AUD 100.00</td>
						</tr>
						{SHOW_ADMINISTRATION_FEE && (
							<tr>
								<td>
									Administration Fee — 10% of the principal only, our charge to the brand, not
									deducted from you
								</td>
								<td>AUD 100.00</td>
							</tr>
						)}
					</tbody>
				</Table>
			</TableWrap>
			<Copy>
				If you told us you are not registered, we pay you AUD 1,000.00
				{SHOW_ADMINISTRATION_FEE
					? ", there is no tax line, and the Administration Fee is still AUD 100.00"
					: " and there is no tax line"}
				. <em>Figures are illustrative.</em>
			</Copy>
			<Subheading>Your responsibilities</Subheading>
			<ClauseList
				clauses={[
					{
						number: "5.5",
						content:
							"Your tax is your responsibility. You are solely responsible for determining, reporting and paying any tax that applies to the amounts we pay you, including income tax, goods and services tax or equivalent, withholding tax, social contributions and anything similar.",
					},
					{
						number: "5.6",
						content:
							"You tell us your tax position; we do not work it out for you. We will not assume you are registered for tax, and we will not apply a rate you have not selected. Any rate we show you is a suggestion based on the standard rate published in your country, and you can change it. You are responsible for the rate you select, including where the standard rate is not the correct rate for your circumstances.",
					},
					{
						number: "5.7",
						content:
							"Changes to your tax status apply going forward only. If your tax registration status or rate changes, update it in your profile. The change applies to Campaigns and Payment Statements from that point onwards. It cannot be applied to a Campaign or a Payment Statement that has already been issued, and it cannot be applied to a payment already made. If you were not registered when a Payment Statement was issued, we cannot re-issue it later with tax added.",
					},
					{
						number: "5.8",
						content:
							"We are not liable. To the maximum extent permitted by law, we are not liable for any tax, penalty, interest or other cost you incur as a result of your tax details, your tax status, or any tax position you take. You release us from any such liability.",
					},
					{
						number: "5.9",
						content:
							"Withholding. We do not calculate or deduct withholding tax. If we are ever required by law to withhold an amount from a payment to you, we will withhold it, pay it to the relevant authority, and give you evidence of the amount withheld.",
					},
				]}
			/>
		</LegalSection>

		<LegalSection number="6" title="Payment Statements">
			<ClauseList clauses={sectionSix} />
		</LegalSection>
		<LegalSection number="7" title="When and how you are paid">
			<ClauseList clauses={sectionSeven} />
		</LegalSection>
		<LegalSection number="8" title="Liability">
			<ClauseList
				clauses={[
					{
						number: "8.1",
						content: `To the maximum extent permitted by law: neither party is liable for Consequential Loss; a party’s liability is reduced proportionately to the extent the other party caused or contributed to it (including a failure to mitigate); and our aggregate liability arising from or in connection with these Payment Terms is limited to ${
							SHOW_ADMINISTRATION_FEE
								? "the total Administration Fees earned by us in respect of your Campaigns in the twelve (12) months immediately preceding the event giving rise to the claim, or SGD 5,000 (whichever is greater)"
								: "SGD 5,000"
						}.`,
					},
					{
						number: "8.2",
						content: "This clause 8 will survive the termination or expiry of these Payment Terms.",
					},
				]}
			/>
		</LegalSection>
		<LegalSection number="9" title="Suspension and Termination">
			<ClauseList
				clauses={[
					{
						number: "9.1",
						content:
							"We may terminate these Payment Terms if: you breach them and do not remedy the breach within 14 days of notice; you breach them and the breach cannot be remedied; we decide to discontinue the Payment Service (on written notice); or you experience an insolvency event.",
					},
					{
						number: "9.2",
						content: (
							<>
								You may terminate these Payment Terms at any time by notifying us through your Account.{" "}
								<strong>Terminating does not affect your Creatorverse platform account</strong> — you
								can continue to use the Platform, you simply cannot be paid through it.
							</>
						),
					},
					{
						number: "9.3",
						content:
							"Termination does not affect any amount already owing to you. We will pay any amount owing for a Campaign you completed before termination.",
					},
				]}
			/>
		</LegalSection>
		<LegalSection number="10" title="General">
			<ClauseList
				clauses={[
					{
						number: "10.1",
						content: (
							<>
								<strong>Assignment:</strong> You may not transfer or assign these Payment Terms without
								our prior written consent. We may assign or transfer them to a third party or to a
								member of our group.
							</>
						),
					},
					{
						number: "10.2",
						content: (
							<>
								<strong>Disputes:</strong> The parties will first meet in good faith to resolve any
								Dispute (except for urgent injunctive relief). If unresolved, it is referred to
								arbitration in Singapore, before one arbitrator, in English, under the SIAC Arbitration
								Rules.
							</>
						),
					},
					{
						number: "10.3",
						content: (
							<>
								<strong>Governing law:</strong> These Payment Terms are governed by the laws of
								Singapore, with exclusive jurisdiction in the courts of Singapore.
							</>
						),
					},
					{
						number: "10.4",
						content: (
							<>
								<strong>Consumer Law Rights:</strong> Nothing in these Payment Terms excludes, restricts
								or modifies any rights or remedies you may have under applicable consumer protection
								legislation, including the Consumer Protection (Fair Trading) Act (Singapore) where
								applicable.
							</>
						),
					},
					{
						number: "10.5",
						content: (
							<>
								<strong>Notices:</strong> Notices to us must be sent to privacy@thecreatorverse.co;
								notices to you will be sent to the email registered against your Account.
							</>
						),
					},
					{
						number: "10.6",
						content: (
							<>
								<strong>Nature of relationship:</strong> These Payment Terms do not create a
								partnership, joint venture, employment or agency relationship. You are an independent
								contractor.
							</>
						),
					},
				]}
			/>
		</LegalSection>
		<LegalSection number="11" title="Definitions">
			<Definitions>
				{SHOW_ADMINISTRATION_FEE && (
					<li>
						Administration Fee — the 10% fee described in clause 4, calculated on the principal amount of
						the Agreed Fee only.
					</li>
				)}
				<li>
					Agreed Fee — the principal amount agreed between you and Creatorverse for your services on a
					Campaign, exclusive of tax.
				</li>
				<li>
					Campaign — an engagement on the Creatorverse platform for which you have accepted Campaign
					Engagement Terms and an Agreed Fee.
				</li>
				<li>
					Consequential Loss — any consequential, special or indirect loss, or loss of profit, revenue,
					business, goodwill, opportunity, savings, reputation, use, or data.
				</li>
				<li>Payment Service — the service described in clause 1.1.</li>
				<li>Payment Statement — the document described in clause 6.</li>
				<li>Project Code — the unique reference issued for each Campaign, shown on your Payment Statement.</li>
				<li>Tax Details — the information described in rows 3 to 5 of the table in clause 3.1.</li>
			</Definitions>
		</LegalSection>
	</Document>
);
